import { z } from 'zod';

export const countries = z.enum(['dk', 'cn', 'us', 'mx', 'hu', 'vn', 'cz', 'pl']);
export const sites = [
  'kom',
  'jia',
  'lva',
  'lva_temp',
  'lom',
  'nyi',
  'lmv',
  'kld',
  'kld_packing',
  'jia_packing',
  'nyi_packing',
  'lom_packing',
  'cph',
  'tru',
  'she',
  'air',
] as const;
export const zSites = z.enum(sites);

export const location = z.object({
  site: zSites,
  area: z.string().optional(),
  description: z.string().optional(),
});

/**
 * Where a printer sits on the production line. LEGO-local metadata, not a
 * Loftware concept, so it is deliberately kept out of the `printer` schema
 * below (which mirrors the Loftware-shaped printer used by the print vendor
 * client and the ingestion service).
 */
export const printerPosition = z.enum(['Left', 'Right']);

/**
 * `printers.workcenter_id` is a Postgres `integer` (signed 32-bit). Anything
 * larger would be rejected by the database as an out-of-range error rather than
 * by validation, so the bound is enforced at the edges instead. Shared so the
 * API and the UI reject exactly the same set of values.
 */
export const MAX_WORKCENTER_ID = 2_147_483_647;

export const workcenterId = z.number().int().positive().max(MAX_WORKCENTER_ID);

export const printer = z.object({
  name: z.string(),
  description: z.string().optional(),
  manufacturer: z.string(),
  model: z.string(),
  cutter: z.boolean().optional(),
  mediaWidth: z.number().optional(),
  mediaHeight: z.number().optional(),
  location: location,
});

export type Location = z.infer<typeof location>;
export type Printer = z.infer<typeof printer>;
export type PrinterPosition = z.infer<typeof printerPosition>;
export type Site = z.infer<typeof zSites>;
export type Country = z.infer<typeof countries>;

export const sitesToCountryMap: Record<Site, Country> = {
  kom: 'dk',
  jia: 'cn',
  lva: 'us',
  lva_temp: 'us',
  lom: 'mx',
  nyi: 'hu',
  lmv: 'vn',
  kld: 'cz',
  kld_packing: 'cz',
  jia_packing: 'cn',
  nyi_packing: 'hu',
  lom_packing: 'mx',
  cph: 'dk',
  tru: 'pl',
  she: 'cn',
  // Dev-only: routed through dedicated AIR_Print / AIR_Printers triggers.
  air: 'dk',
};
